import React from "react";
import PropTypes from "prop-types";
import MuiTextField from "@material-ui/core/TextField";
import InputAdornment from "../InputAdornment";
import { styles as formControlStyles } from "../FormControl";
import { styles as outlinedStyles } from "../OutlinedInput";
import { styles as underlinedStyles } from "../UnderlinedInput";
import { styles as inputLabelStyles } from "../InputLabel";
import { styles as formHelperTextStyles } from "../FormHelperText";
import withStyles from "../../styles/withStyles";

function TextField({
  classes,
  select, // ignore - we don't want to render this type of select
  SelectProps, // ignore - we don't want to render this type of select
  readOnly,
  variant,
  InputProps,
  InputLabelProps,
  FormHelperTextProps,
  ...props
}) {
  let inputClasses = {};
  let formHelperTextClasses = {};
  if (variant === "underlined") {
    inputClasses = splitStyles(classes, "underlined");
  } else {
    inputClasses = splitStyles(classes, "outlined");
    formHelperTextClasses = splitStyles(classes, "formHelperText");
  }
  let inputLabelClasses = splitStyles(classes, "inputLabel");

  // adornment styling
  if (InputProps) {
    if (isStringOrSvg(InputProps.startAdornment)) {
      InputProps.startAdornment = (
        <InputAdornment position="start">
          {InputProps.startAdornment}
        </InputAdornment>
      );
    }

    if (isStringOrSvg(InputProps.endAdornment)) {
      InputProps.endAdornment = (
        <InputAdornment position="end">
          {InputProps.endAdornment}
        </InputAdornment>
      );
    }
  }

  return (
    <MuiTextField
      variant={variant === "underlined" ? "standard" : "outlined"}
      classes={splitStyles(classes, "formControl")}
      InputProps={{
        ...(variant === "outlined" ? { notched: false } : {}),
        ...InputProps,
        readOnly,
        classes: Object.assign({}, inputClasses, InputProps.classes)
      }}
      InputLabelProps={{
        ...InputLabelProps,
        classes: Object.assign({}, inputLabelClasses, InputLabelProps.classes)
      }}
      FormHelperTextProps={{
        ...FormHelperTextProps,
        classes: Object.assign(
          {},
          formHelperTextClasses,
          FormHelperTextProps.classes
        )
      }}
      {...props}
    />
  );
}

// This helper function ensures only strings and svgs are wrapped in the
// InputAdornment component to ensure consistent styling throughout our app
function isStringOrSvg(value) {
  return (
    !!value &&
    (typeof value === "string" ||
      (value.type &&
        (value.type === "svg" || value.type.muiName === "SvgIcon")))
  );
}

// classes prop >> various component styles:
// now that it has been combined in withStyles(), split back
// out into own component styles
function splitStyles(classes, prefix) {
  return Object.keys(classes).reduce((acc, curr) => {
    let split = curr.split("-");
    if (split[0] === prefix) {
      return Object.assign(acc, { [split[1]]: classes[curr] });
    }
    return acc;
  }, {});
}

// various component styles >> classes prop:
// prefix the styles with the corresponding component type
// just in case there are multiple duplicate keys before
// passing it to withStyles()
function prefixStyles(prefix, styles) {
  return Object.keys(styles).reduce(
    (acc, curr) => Object.assign(acc, { [`${prefix}-${curr}`]: styles[curr] }),
    {}
  );
}

let styles = theme => ({
  ...prefixStyles("formControl", formControlStyles(theme)),
  ...prefixStyles("outlined", outlinedStyles(theme)),
  ...prefixStyles("underlined", underlinedStyles(theme)),
  ...prefixStyles("inputLabel", inputLabelStyles(theme)),
  ...prefixStyles("formHelperText", formHelperTextStyles(theme))
});

export default withStyles(styles)(TextField);

TextField.propTypes = {
  variant: PropTypes.string,
  InputProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  FormHelperTextProps: PropTypes.object
};

TextField.defaultProps = {
  readOnly: false,
  variant: "outlined",
  InputProps: {},
  InputLabelProps: {},
  FormHelperTextProps: {}
};
